import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Shield, Truck, RotateCcw, Headphones, FileDown } from "lucide-react";
import { api, API, formatApiErrorDetail, track } from "../lib/api";
import { toast } from "sonner";

export const NewsletterForm = ({ source = "footer", dark = false, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/newsletter", { email, source });
      toast.success(data.message);
      localStorage.setItem("shodan_email", email.trim().toLowerCase());
      setDone(true);
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  if (done) return (
    <div data-testid="newsletter-success" className={`text-sm font-semibold ${dark ? "text-[#8FA84E]" : "text-[#4A5D23]"}`}>
      <p>You're in! Your 10% off code:</p>
      <p className={`mt-2 inline-block border-2 border-dashed px-4 py-2 font-mono font-bold text-base tracking-widest ${dark ? "border-[#8FA84E] text-white" : "border-[#4A5D23] text-black"}`} data-testid="newsletter-coupon-code">WELCOME10</p>
      <p className={`mt-2 text-xs font-normal ${dark ? "text-white/50" : "text-black/50"}`}>Use it at checkout anytime — keep it handy.</p>
      <a data-testid="checklist-download-link" href={`${API}/checklist`} target="_blank" rel="noopener noreferrer"
        onClick={() => track("checklist_download", source)}
        className={`mt-3 inline-flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-150 ${dark ? "bg-[#4A5D23] text-white hover:bg-[#3D4D1D]" : "bg-[#121212] text-white hover:bg-[#4A5D23]"}`}>
        <FileDown size={15} /> Download Your Preparedness Checklist
      </a>
      <p className={`mt-2 text-xs font-normal ${dark ? "text-white/50" : "text-black/50"}`}>Also on its way to your inbox.</p>
    </div>
  );
  return (
    <form onSubmit={submit} className="flex flex-col gap-2 max-w-md w-full">
      <input data-testid="newsletter-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        className={`w-full px-4 py-3 text-sm border focus:outline-none focus:ring-2 focus:ring-[#4A5D23] ${dark ? "bg-white/10 border-white/20 text-white placeholder:text-white/50" : "bg-white border-black/20"}`} />
      <button data-testid="newsletter-submit" type="submit" className="w-full bg-[#4A5D23] text-white px-5 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150 whitespace-nowrap">
        Get the Free Checklist
      </button>
    </form>
  );
};

const TRUST = [
  { icon: Shield, title: "Secure Checkout", text: "Your information is protected" },
  { icon: Truck, title: "Fast Shipping", text: "Quick dispatch from the USA" },
  { icon: RotateCcw, title: "Easy Returns", text: "30-day hassle-free returns" },
  { icon: Headphones, title: "Customer Support", text: "Real help from real people" },
];

export const Footer = () => (
  <footer className="bg-[#121212] text-white">
    <div className="border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {TRUST.map((t) => (
          <div key={t.title} className="flex items-start gap-3">
            <t.icon size={22} className="text-[#8FA84E] shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold uppercase tracking-widest">{t.title}</p>
              <p className="text-xs text-white/60 mt-1">{t.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 grid grid-cols-2 md:grid-cols-5 gap-10 text-sm">
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">Shop</h4>
        {[["Tactical Gear", "/shop?category=tactical-gear"], ["Clothing", "/shop?category=clothing"], ["Personal Security", "/shop?category=personal-security"], ["Preparedness", "/shop?category=preparedness"], ["Outdoor & Survival", "/shop?category=outdoor-survival"], ["Everyday Carry", "/shop?category=everyday-carry"], ["Home Security", "/shop?category=home-security"], ["Footwear", "/shop?category=footwear"], ["Ready Kits", "/kits"]].map(([l, to]) => (
          <Link key={l} to={to} className="block py-1 text-white/80 hover:text-[#8FA84E] transition-colors duration-150">{l}</Link>
        ))}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">Information</h4>
        {[["About Shodan Security", "/about"], ["Shipping", "/about#shipping"], ["Returns", "/about#returns"], ["FAQ", "/contact"], ["Privacy", "/about#privacy"], ["Terms", "/about#terms"]].map(([l, to]) => (
          <Link key={l} to={to} className="block py-1 text-white/80 hover:text-[#8FA84E] transition-colors duration-150">{l}</Link>
        ))}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">Resources</h4>
        {[["Field Guide", "/field-guide"], ["Preparedness Club", "/club"], ["Track Order", "/track"], ["Events", "/events"], ["Show Special", "/show"]].map(([l, to]) => (
          <Link key={l} to={to} className="block py-1 text-white/80 hover:text-[#8FA84E] transition-colors duration-150">{l}</Link>
        ))}
      </div>
      <div>
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">Customer Service</h4>
        {[["Contact", "/contact"], ["Support", "/contact"], ["Order Tracking", "/account"]].map(([l, to]) => (
          <Link key={l + to} to={to} className="block py-1 text-white/80 hover:text-[#8FA84E] transition-colors duration-150">{l}</Link>
        ))}
        <div className="flex gap-4 mt-5">
          <a href="https://www.instagram.com/shodansecurity/" data-testid="social-instagram" target="_blank" rel="noreferrer" aria-label="Instagram" className="text-white/60 hover:text-[#8FA84E] transition-colors duration-150"><Instagram size={18} /></a>
          <a href="https://www.facebook.com/profile.php?id=61594231430695" data-testid="social-facebook" target="_blank" rel="noreferrer" aria-label="Facebook" className="text-white/60 hover:text-[#8FA84E] transition-colors duration-150"><Facebook size={18} /></a>
          <a href="https://www.youtube.com/channel/UCy_z6C7NQdPyJx94Kib2WHQ" data-testid="social-youtube" target="_blank" rel="noreferrer" aria-label="YouTube" className="text-white/60 hover:text-[#8FA84E] transition-colors duration-150"><Youtube size={18} /></a>
        </div>
      </div>
      <div className="col-span-2 md:col-span-1">
        <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 mb-4">The Preparedness Checklist</h4>
        <p className="text-xs text-white/60 mb-3">Get 10% off your first order (code WELCOME10) plus the free readiness checklist.</p>
        <NewsletterForm source="footer" dark />
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-white/40 px-4">
      <img src="/logo.png" alt="Shodan Security logo" className="h-14 w-14 object-contain mx-auto mb-2 bg-white rounded-full" />
      <p className="font-heading font-black uppercase tracking-tighter text-white/60 mb-1">SHODAN <span className="text-[#8FA84E]">SECURITY</span></p>
      <p>Protect. Prepare. Perform. — © 2026 Shodan Security. All rights reserved.</p>
      <p className="mt-1">Products sold subject to state and local regulations. Check your local laws before purchasing restricted items.</p>
    </div>
  </footer>
);
